import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, INVALID_ID } from "../common";
import { ScheduleInfo } from "./model";
import { getToken } from "../../features/user/UserAvatar";

export interface ScheduleDeleteRequest {
  scheduleId: string;
}
export const scheduleDelete = createAsyncThunk(
  "scheduleDelete",
  async (param: ScheduleDeleteRequest, thunkAPI) => {
    console.debug("delete schedule");
    const response = await fetch(`${getBackendUrl()}/api/schedule/${param.scheduleId}`, {
      method: "DELETE",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      }
    });
    if (response.ok) {
      return { scheduleId: param.scheduleId };
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export interface ScheduleInsUpdState {
  state: LoadStateInfo;
  schedule: ScheduleInfo;
}

const initialState: ScheduleInsUpdState = {
  state: notStarted(),
  schedule: emptySchedule(),
};

function emptySchedule(): ScheduleInfo {
  return {
    id: Number(INVALID_ID),
    schoolId: Number(INVALID_ID),
    classroomId: Number(INVALID_ID),
    subjectId: Number(INVALID_ID),
    dayOfWeek: 1,
    startTime: "08:00",
    endTime: "09:00",
  };
}

export interface ScheduleInsertRequest {
  schedule: ScheduleInfo;
}
export const scheduleInsert = createAsyncThunk(
  "scheduleInsert",
  async (param: ScheduleInsertRequest, thunkAPI) => {
    console.debug("insert schedule");
    const response = await fetch(`${getBackendUrl()}/api/schedule`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.schedule)
    });
    if (response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export interface ScheduleUpdateRequest {
  scheduleId: number;
  schedule: ScheduleInfo;
}
export const scheduleUpdate = createAsyncThunk(
  "scheduleUpdate",
  async (param: ScheduleUpdateRequest, thunkAPI) => {
    console.debug("update schedule");
    const response = await fetch(`${getBackendUrl()}/api/schedule/${param.scheduleId}`, {
      method: "PUT",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.schedule)
    });
    if (response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export const scheduleInsUpdSlice = createSlice({
  name: "scheduleInsUpd",
  initialState,
  reducers: {
    set: (state, action: PayloadAction<ScheduleInfo>) => {
      state.state = notStarted();
      state.schedule = action.payload;
    },
    clean: (state) => {
      state.state = notStarted();
      state.schedule = emptySchedule();
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(scheduleInsert.pending, (state) => {
        state.state = loading();
      })
      .addCase(scheduleInsert.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.schedule = action.payload;
      })
      .addCase(scheduleInsert.rejected, (state, action) => {
        state.state = error(action);
      })
      .addCase(scheduleUpdate.pending, (state) => {
        state.state = loading();
      })
      .addCase(scheduleUpdate.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.schedule = action.payload;
      })
      .addCase(scheduleUpdate.rejected, (state, action) => {
        state.state = error(action);
      })
      .addCase(scheduleDelete.pending, (state) => {
        state.state = loading();
      })
      .addCase(scheduleDelete.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.schedule = emptySchedule();
      })
      .addCase(scheduleDelete.rejected, (state, action) => {
        state.state = error(action);
      });
  },
});

export const selectScheduleInsUpd = (state: RootState) => state.scheduleInsUpd;

export default scheduleInsUpdSlice.reducer;
